import React, { useState, useEffect } from 'react';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { DeliveryDriver, User, OpeningHours } from '../../types';
import { db } from '../../firebase/client';
import { collection, query, where, getDocs, doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useAllOrders } from '../../context/AllOrdersContext';
import { useNotifications } from '../../context/NotificationContext';
import { formatDateReadable, getTimeRemaining } from '../../utils/dateUtils';
import { AppContext } from '../../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Rocket, Clock, CreditCard, History as HistoryIcon, Plus, Zap, ShieldCheck, ArrowLeft, ChevronRight, Star, Trash2, HelpCircle, Store, MapPin, ExternalLink, Phone, Save, Loader2, Calendar } from 'lucide-react';
const AdminAccountScreen: React.FC = () => {
    const { currentUser, logout, requestNotificationPermission, notificationPermission } = useAuth();
    const { 
        restaurants, 
        getRestaurantById, 
        updateRestaurantDetails, 
        loading: dataLoading, 
        wilayas, 
        uploadImage,
        calculateRestaurantRevenue,
        switchToCommissionPlan
    } = useData();
    const { getCurrentPosition } = useGeolocation();
    const { view, setView } = React.useContext(AppContext);

    const { placedOrders } = useAllOrders();

    // --- FINANCIAL CALCULATIONS ---
    const calculateCommission = () => {
        if (!restaurant) return 0;
        const commStart = restaurant.commissionStartedAt?.toDate ? restaurant.commissionStartedAt.toDate() : (restaurant.commissionStartedAt ? new Date(restaurant.commissionStartedAt) : null);
        
        const deliveredOrders = placedOrders?.filter(o => {
            if (o.restaurantId !== restaurant.id) return false;
            
            // Financial Scope: Only delivered or completed orders
            if (o.status !== 'Livrée' && o.status !== 'Terminée') return false;
            
            if (commStart) {
                const ts = o.createdAt || (o as any).serverCreatedAt || (o as any).placedAt;
                const d = ts instanceof Date ? ts : (ts?.seconds ? new Date(ts.seconds * 1000) : ts ? new Date(ts) : null);
                // Only count orders since the last commission payment/activation
                // Add a small 100ms buffer to avoid micro-race-conditions during validation
                if (!d || !commStart || d.getTime() < (commStart.getTime() + 100)) return false;
            }
            return true;
        }) || [];

        const totalTurnover = deliveredOrders.reduce((sum, o) => sum + (o.totalPrice - (o.deliveryFee || 0)), 0);
        const currentMonthCommission = Math.round(totalTurnover * 0.05);
        
        // Ensure debt is treated as a number
        const debt = Number(restaurant.commissionDebt || 0);
        return Math.max(0, currentMonthCommission + debt);
    };

    const restaurant = getRestaurantById(currentUser?.restaurantId || '') || 
        restaurants.find(r => r.ownerId === currentUser?.uid || r.owner_id === currentUser?.uid);

    const isFeaturedActive = React.useMemo(() => {
        if (!restaurant || restaurant.featuredStatus !== 'active' || !restaurant.featuredExpiry) return false;
        const expiry = restaurant.featuredExpiry.toDate ? restaurant.featuredExpiry.toDate() : new Date(restaurant.featuredExpiry);
        return expiry > new Date();
    }, [restaurant]);

    // Calcul frais Yumii du mois courant — basé sur les commandes déjà chargées en mémoire pour plus de fiabilité
    const currentFees = React.useMemo(() => {
        if (!restaurant || restaurant.subscriptionType !== 'commission') return 0;
        
        const commStart = restaurant.commissionStartedAt?.toDate 
            ? restaurant.commissionStartedAt.toDate() 
            : (restaurant.commissionStartedAt ? new Date(restaurant.commissionStartedAt) : null);
            
        const deliveredOrders = (placedOrders || []).filter(o => {
            if (o.restaurantId !== restaurant.id && (o as any).id_restaurant !== restaurant.id) return false;
            if (o.status !== 'Livrée' && o.status !== 'Terminée') return false;
            
            if (commStart) {
                const ts = o.createdAt || (o as any).timestamp || (o as any).serverCreatedAt;
                const d = ts instanceof Date ? ts : (ts?.seconds ? new Date(ts.seconds * 1000) : ts ? new Date(ts) : null);
                // On ajoute un tampon de 100ms pour éviter les soucis de précision au moment de la validation
                if (!d || d.getTime() < (commStart.getTime() + 100)) return false;
            }
            return true;
        });

        const totalTurnover = deliveredOrders.reduce((sum, o) => {
            const price = Number(o.totalPrice || 0);
            const delivery = Number(o.deliveryFee || 0);
            return sum + (price - delivery);
        }, 0);

        return Math.round(totalTurnover * 0.05);
    }, [placedOrders, restaurant?.id, restaurant?.subscriptionType, restaurant?.commissionStartedAt]);

    const [formData, setFormData] = useState({
        ownerName: '', // Added: Nom du propriétaire
        name: '',
        slogan: '',
        phone: '',
        wilaya: 'Alger',
        image: '',
        gallery: [] as string[],
        deliveryEnabled: false,
        globalDiscount: 0,
        isOnline: true,
        useSchedule: false,
        openingHours: {
             monday: { open: '09:00', close: '23:00', isClosed: false },
             tuesday: { open: '09:00', close: '23:00', isClosed: false },
             wednesday: { open: '09:00', close: '23:00', isClosed: false },
             thursday: { open: '09:00', close: '23:00', isClosed: false },
             friday: { open: '09:00', close: '23:00', isClosed: false },
             saturday: { open: '09:00', close: '23:00', isClosed: false },
             sunday: { open: '09:00', close: '23:00', isClosed: false },
         } as OpeningHours,
        location: undefined as { lat: number; lng: number } | undefined,
        deliveryDrivers: [] as DeliveryDriver[],
        tableReservationEnabled: false,
        reservationSettings: {
            totalTables: 10,
            maxCapacityPerSlot: 40,
            slotDurationMinutes: 90,
            advanceNoticeMin: 60
        }
    });
    const [newImageUrl, setNewImageUrl] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [freshDriverRatings, setFreshDriverRatings] = useState<Record<string, { rating: number; ratingCount: number }>>({});

    useEffect(() => {
        const fetchRatings = async () => {
            if (formData.deliveryDrivers.length === 0) return;

            const ratingsMap: Record<string, { rating: number; ratingCount: number }> = {};
            try {
                const promises = formData.deliveryDrivers.map(async (d) => {
                    const dRef = doc(db, 'users', d.id);
                    const dSnap = await getDoc(dRef);
                    if (dSnap.exists()) {
                        const data = dSnap.data();
                        ratingsMap[d.id] = {
                            rating: data.rating || 0,
                            ratingCount: data.ratingCount || 0
                        };
                    }
                });
                await Promise.all(promises);
                setFreshDriverRatings(ratingsMap);
            } catch (err) {
                console.error("Error fetching fresh driver ratings:", err);
            }
        };
        fetchRatings();
    }, [formData.deliveryDrivers]);

    // Accordion state - default to 'identity' open
    const [expandedSection, setExpandedSection] = useState<'identity' | 'delivery' | 'promotions' | 'hours' | 'reservations' | 'subscription' | null>(null);
    const [subscriptionView, setSubscriptionView] = useState<'none' | 'renew' | 'change' | 'pay_fees'>('none');
    const [showBoostOptions, setShowBoostOptions] = useState(false);
    const [isCalculatingFee, setIsCalculatingFee] = useState(false);
    const [isProcessingPlan, setIsProcessingPlan] = useState(false);
    const [hasPendingPayment, setHasPendingPayment] = useState(false);
    const [isUpdatingOnline, setIsUpdatingOnline] = useState(false);
    const [pendingPaymentInfo, setPendingPaymentInfo] = useState<any>(null);

    // Monitor pending payments for early locking
    useEffect(() => {
        if (!restaurant?.id) return;
        const q = query(
            collection(db, 'payments'), 
            where('restaurantId', '==', restaurant.id),
            where('status', '==', 'pending')
        );
        const unsub = onSnapshot(q, (snap) => {
            setHasPendingPayment(!snap.empty);
            if (!snap.empty) {
                setPendingPaymentInfo(snap.docs[0].data());
            } else {
                setPendingPaymentInfo(null);
            }
        });
        return () => unsub();
    }, [restaurant?.id]);

    const toggleSection = (section: 'identity' | 'delivery' | 'promotions' | 'hours' | 'reservations' | 'subscription') => {
        setExpandedSection(prev => prev === section ? null : section);
    };

    const [initialFormData, setInitialFormData] = useState<typeof formData | null>(null);

    useEffect(() => {
        if (restaurant) {
            const data = {
                ownerName: restaurant.ownerName || currentUser?.name || '', // Fallback to user name
                name: restaurant.name || '',
                slogan: restaurant.slogan || '',
                phone: restaurant.phone || currentUser?.phone || '', // Fallback to user phone
                wilaya: restaurant.wilaya || currentUser?.wilaya || 'Alger', // Fallback to user wilaya
                image: restaurant.image || '',
                gallery: restaurant.gallery || [],
                deliveryEnabled: restaurant.deliveryEnabled ?? false,
                globalDiscount: restaurant.globalDiscount ?? 0,
                isOnline: restaurant.isOnline ?? true,
                useSchedule: restaurant.useSchedule ?? false,
                openingHours: restaurant.openingHours || {
                    monday: { open: '09:00', close: '23:00', isClosed: false },
                    tuesday: { open: '09:00', close: '23:00', isClosed: false },
                    wednesday: { open: '09:00', close: '23:00', isClosed: false },
                    thursday: { open: '09:00', close: '23:00', isClosed: false },
                    friday: { open: '09:00', close: '23:00', isClosed: false },
                    saturday: { open: '09:00', close: '23:00', isClosed: false },
                    sunday: { open: '09:00', close: '23:00', isClosed: false },
                },
                location: restaurant.location || undefined,
                deliveryDrivers: restaurant.deliveryDrivers || [],
                tableReservationEnabled: restaurant.tableReservationEnabled ?? false,
                reservationSettings: restaurant.reservationSettings || {
                    totalTables: 10,
                    maxCapacityPerSlot: 40,
                    slotDurationMinutes: 90,
                    advanceNoticeMin: 60
                }
            };
            setFormData(data);
            setInitialFormData(data);
        }
    }, [restaurant, currentUser]);

    const isDirty = initialFormData && JSON.stringify(formData) !== JSON.stringify(initialFormData);

    const getTrialDaysLeft = () => {
        if (!restaurant?.trialExpiry) return 0;
        const now = new Date();
        // Handle both Firestore Timestamp and JS Date
        const expiryDate = (restaurant.trialExpiry as any).seconds 
            ? new Date((restaurant.trialExpiry as any).seconds * 1000)
            : new Date(restaurant.trialExpiry);
        const diffTime = expiryDate.getTime() - now.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const trialDaysLeft = getTrialDaysLeft();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        let value: any = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
        const name = e.target.name;

        // Convert to number for specific fields to match Firebase data types
        if (name === 'globalDiscount') {
            value = Number(value) || 0;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleToggleOnline = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!restaurant) return;
        const newStatus = e.target.checked;
        
        // Update local state for immediate UI feedback
        setFormData(prev => ({ ...prev, isOnline: newStatus }));
        
        try {
            setIsUpdatingOnline(true);
            await updateRestaurantDetails(restaurant.id, { isOnline: newStatus });
        } catch (error) {
            // Revert on error
            setFormData(prev => ({ ...prev, isOnline: !newStatus }));
            alert("Erreur lors de la mise à jour du statut.");
        } finally {
            setIsUpdatingOnline(false);
        }
    };

    const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        try {
            setIsUploadingCover(true);
            const url = await uploadImage(file);
            setFormData(prev => ({ ...prev, image: url }));
        } catch (error: any) {
            // Handled in context
        } finally {
            setIsUploadingCover(false);
            e.target.value = '';
        }
    };

    const handleSearchDrivers = async () => {
        const rawTerm = searchTerm.trim();
        if (!rawTerm) return;
        setIsSearching(true);
        setSearchResults([]);

        try {
            const usersRef = collection(db, 'users');

            // Normalize phone number for search
            // If it's a number, we try to match different formats
            let phoneToSearch = rawTerm.replace(/\s+/g, ''); // strip spaces

            // Strategy: 
            // 1. Try exact match
            // 2. If it starts with 0, try without leading 0
            // 3. If it doesn't start with 0 and is likely a phone, try with leading 0

            const searchQueries = [];

            const wilayaFilter = where('wilaya', '==', formData.wilaya);

            // 1. Base search (exact match)
            searchQueries.push(query(usersRef, where('role', '==', 'livreur'), where('phone', '==', phoneToSearch), wilayaFilter));

            // 2. Format variants for Algerian numbers
            if (phoneToSearch.startsWith('0')) {
                const withoutZero = phoneToSearch.substring(1);
                searchQueries.push(query(usersRef, where('role', '==', 'livreur'), where('phone', '==', withoutZero), wilayaFilter));
            } else if (/^[567]\d+/.test(phoneToSearch)) {
                searchQueries.push(query(usersRef, where('role', '==', 'livreur'), where('phone', '==', '0' + phoneToSearch), wilayaFilter));
            }

            // Execute phone queries
            const phoneSnapshots = await Promise.all(searchQueries.map(q => getDocs(q)));
            let resultsMap = new Map();

            phoneSnapshots.forEach(snap => {
                snap.docs.forEach(doc => {
                    const data = doc.data() as User;
                    resultsMap.set(doc.id, { ...data, uid: doc.id });
                });
            });

            // If no phone results, try name search as fallback
            if (resultsMap.size === 0) {
                const qName = query(
                    usersRef,
                    where('role', '==', 'livreur'),
                    where('name', '==', rawTerm),
                    wilayaFilter
                );
                const snapshotName = await getDocs(qName);
                snapshotName.docs.forEach(doc => {
                    const data = doc.data() as User;
                    resultsMap.set(doc.id, { ...data, uid: doc.id });
                });
            }

            setSearchResults(Array.from(resultsMap.values()));
        } catch (err) {
            console.error("Erreur recherche:", err);
            alert("Erreur lors de la recherche du livreur.");
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddDriver = async (driver: User) => {
        if (!restaurant) return;
        if (formData.deliveryDrivers.some(d => d.id === driver.uid)) {
            alert("Ce livreur est déjà dans votre liste.");
            return;
        }
        const newDriver: DeliveryDriver = {
            id: driver.uid,
            name: driver.name,
            phone: driver.phone || ''
        };
        const updatedDrivers = [...(formData.deliveryDrivers || []), newDriver];
        setFormData(prev => ({ ...prev, deliveryDrivers: updatedDrivers }));
        await updateRestaurantDetails(restaurant.id, { deliveryDrivers: updatedDrivers });
        setSearchTerm('');
        setSearchResults([]);
    };

    const handleDeleteDriver = async (index: number) => {
        if (!restaurant) return;
        const updatedDrivers = formData.deliveryDrivers.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, deliveryDrivers: updatedDrivers }));
        await updateRestaurantDetails(restaurant.id, { deliveryDrivers: updatedDrivers });
    };

    const handleSaveChanges = async (e?: React.FormEvent | React.MouseEvent) => {
        e?.preventDefault();
        if (!restaurant) return;
        setIsSaving(true);

        console.log("Saving restaurant details for:", restaurant.id);

        try {
            // DEEP CLEAN Helper to prevent Firestore "Technical Error" (NaN/undefined/empty)
            const cleanObj = (obj: any): any => {
                const newObj: any = {};
                Object.keys(obj).forEach(key => {
                    const value = obj[key];
                    if (value === undefined || value === null) return;
                    if (typeof value === 'number' && isNaN(value)) return;
                    
                    if (Array.isArray(value)) {
                        newObj[key] = value.map(v => (typeof v === 'object' && v !== null) ? cleanObj(v) : v);
                    } else if (typeof value === 'object' && value !== null && !(value instanceof Date)) {
                        newObj[key] = cleanObj(value);
                    } else {
                        newObj[key] = value;
                    }
                });
                return newObj;
            };

            const cleanedData = cleanObj({
                ...formData,
                globalDiscount: Number(formData.globalDiscount) || 0,
                isOnline: formData.isOnline,
                deliveryEnabled: formData.deliveryEnabled ?? false
            });

            // Extra safety for location which is critical
            if (cleanedData.location && (isNaN(cleanedData.location.lat) || isNaN(cleanedData.location.lng))) {
                delete cleanedData.location;
            }

            await updateRestaurantDetails(restaurant.id, cleanedData);
            
            // Sync local states with what was actually saved (cleaned types) 
            // to ensure isDirty becomes false immediately
            const syncData = { ...formData, ...cleanedData };
            setInitialFormData(syncData);
            setFormData(syncData);
            
            alert("Modifications enregistrées ! ✅");
        } catch (error: any) {
            console.error("Erreur critique save:", error);
            alert("Erreur technique : " + (error.message || "vérifiez votre connexion."));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCommissionRenewal = async () => {
        if (!restaurant) return;
        setIsCalculatingFee(true);
        try {
            const now = new Date();
            const lastMonth = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            const commStart = restaurant.commissionStartedAt?.toDate ? restaurant.commissionStartedAt.toDate() : (restaurant.commissionStartedAt ? new Date(restaurant.commissionStartedAt) : null);
            const revenue = await calculateRestaurantRevenue(restaurant.id, lastMonth, now, commStart || undefined);
            const fee = Math.max(0, revenue * 0.05);
            const debt = Number(restaurant.commissionDebt || 0);
            const total = fee + debt;
            
            setView({ screen: 'adminSubscription', params: { 
                planType: 'commission', 
                amount: total,
                isRenewal: true,
                mode: 'renew',
                description: `Règlement commission (${fee.toLocaleString()} DA) + Dette (${debt.toLocaleString()} DA)`
            } });
            setSubscriptionView('none');
        } catch (err) {
            alert("Erreur lors du calcul des frais.");
        } finally {
            setIsCalculatingFee(false);
        }
    };



    if (dataLoading) return <div className="p-4">Chargement du profil...</div>;
    if (!restaurant) return <div className="p-4 text-red-600">Erreur: Restaurant introuvable.</div>;

    return (
        <div className="p-4 bg-gray-50 min-h-screen pb-24">
            <h1 className="text-2xl font-black text-gray-900 uppercase italic tracking-tighter leading-none mt-4 mb-1">Mon Établissement</h1>
            <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mb-6">Gérez les informations de votre restaurant.</p>
            

            <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[2rem] shadow-sm overflow-hidden border border-red-50 p-6 mb-6 transition-all hover:shadow-md relative"
            >
                <div className="flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center text-red-600 shadow-sm border border-red-100">
                            <span className="text-2xl">🔔</span>
                        </div>
                        <div className="text-left">
                            <h2 className="text-sm font-black text-gray-900 uppercase tracking-tight italic">
                                {notificationPermission === 'granted' ? 'Notifications Activées' : 'Activer les alertes'}
                            </h2>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide leading-tight mt-0.5">
                                {notificationPermission === 'granted'
                                    ? 'Alertes instantanées pour chaque commande.'
                                    : 'Soyez alerté dès qu\'un client commande.'}
                            </p>
                        </div>
                    </div>
                    {notificationPermission !== 'granted' && (
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={requestNotificationPermission}
                            className="bg-red-600 text-white px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-widest italic shadow-lg shadow-red-100"
                        >
                            Activer
                        </motion.button>
                    )}
                </div>
            </motion.div>
            
            {/* Mode Alarme - Ultra-Compact Notice */}
            <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center gap-3 px-4 py-3 bg-gray-100/30 rounded-2xl border border-gray-200/50 mb-8"
            >
                <span className="text-sm opacity-40">⚡</span>
                <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter flex-1 leading-tight">
                    <span className="text-orange-600/60 font-black">Mode Alarme :</span> Paramètres &gt; Apps &gt; Yumii &gt; Notifications &gt; Alerts &gt; <span className="text-gray-600 underline decoration-gray-300">Ignorer Ne pas déranger.</span>
                </p>
                {Capacitor.isNativePlatform() && (
                    <button 
                        onClick={() => {
                            if (window.confirm("Ouvrir les réglages systeme ?")) {
                                window.location.href = "app-settings:";
                            }
                        }}
                        className="text-[8px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-1 rounded-lg border border-blue-100 active:scale-95"
                    >
                        Réglages
                    </button>
                )}
            </motion.div>

            {/* --- NOUVELLE INTERFACE ABONNEMENT (STYLE PREMIUM COLLAPSIBLE) --- */}
            <div className="mb-6">
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100">
                    <button 
                        type="button" 
                        onClick={() => toggleSection('subscription')} 
                        className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all"
                    >
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-[#E31E24] flex items-center justify-center text-white shadow-lg shadow-red-200">
                                <CreditCard className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-lg font-black text-gray-900 uppercase italic tracking-tighter">Abonnements</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight">
                                        {restaurant.subscriptionStatus === 'trial' ? "Essai Gratuit" : 
                                         restaurant.subscriptionType === 'mensuel' ? "Mensuel" :
                                         restaurant.subscriptionType === 'annuel' ? "Annuel" :
                                         restaurant.subscriptionType === 'commission' ? "Commission" : "Aucun"}
                                    </p>
                                    <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                    <p className="text-xs font-black text-red-600 uppercase italic tracking-tighter">
                                        {(() => {
                                            const expiry = restaurant.currentPeriodExpiry || restaurant.subscriptionExpiry || restaurant.trialExpiry;
                                            return getTimeRemaining(expiry) || 'Expiré';
                                        })()}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'subscription' ? 'bg-[#E31E24] text-white rotate-180 shadow-md shadow-red-100' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {expandedSection === 'subscription' && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-8 border-t border-gray-50 space-y-8">
                                    
                                    {/* 1. ABONNEMENT PRINCIPAL */}
                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between px-2">
                                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Plan Principal</h3>
                                            {restaurant.subscriptionStatus === 'active' && (
                                                <span className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-600 rounded-full text-[10px] font-black uppercase italic border border-green-100">
                                                    <CheckCircle2 size={12} /> Actif
                                                </span>
                                            )}
                                        </div>
                                        
                                        {subscriptionView !== 'change' && (
                                            <motion.div 
                                                initial={{ x: -20, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                className="bg-[#E31E24] rounded-3xl p-6 shadow-xl relative z-20 border-b-4 border-black/10 overflow-hidden"
                                            >
                                                <div className="flex justify-between items-start mb-4">
                                                    <div className="flex flex-col">
                                                        <span className="text-[10px] font-black text-white/70 uppercase tracking-widest mb-1">
                                                            {restaurant.subscriptionStatus === 'expired' ? "STATUT DE L'ABONNEMENT" :
                                                            restaurant.subscriptionStatus === 'trial' ? "PÉRIODE D'ESSAI" : "PLAN ACTUEL"}
                                                        </span>
                                                        <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">
                                                            {restaurant.subscriptionStatus === 'expired' ? "Abonnement Expiré" :
                                                            restaurant.subscriptionStatus === 'trial' ? "Essai Gratuit" : 
                                                            restaurant.subscriptionType === 'mensuel' ? "Mensuel" :
                                                            restaurant.subscriptionType === 'annuel' ? "Annuel" :
                                                            restaurant.subscriptionType === 'commission' ? "Commission" : "Aucun"}
                                                        </h3>
                                                        {(restaurant.currentPeriodExpiry || restaurant.subscriptionExpiry || restaurant.trialExpiry) && (
                                                            <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-1">
                                                                Expire le {formatDateReadable(restaurant.currentPeriodExpiry || restaurant.subscriptionExpiry || restaurant.trialExpiry)}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="bg-white/20 p-2 rounded-xl backdrop-blur-md">
                                                        <ShieldCheck size={20} className="text-white" />
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center justify-between mt-6">
                                                    <span className="text-4xl font-black text-white italic tracking-tighter">
                                                        {restaurant.subscriptionType === 'commission' ? (
                                                            `${(currentFees + Number(restaurant.commissionDebt || 0)).toLocaleString()} DA`
                                                        ) : (
                                                                getTimeRemaining(restaurant.currentPeriodExpiry) || 'Expiré'
                                                        )}
                                                    </span>
                                                    <div className="flex flex-col items-end">
                                                        <span className="text-[8px] font-bold text-white/60 uppercase">
                                                            {restaurant.subscriptionType === 'commission' ? 'FRAIS YUMII DUS' : 'Échéance'}
                                                        </span>
                                                        <span className="text-[10px] font-black text-white tabular-nums">
                                                            {restaurant.subscriptionType === 'commission' ? (
                                                                Number(restaurant.commissionDebt || 0) > 0 ? `Inclut ${Number(restaurant.commissionDebt).toLocaleString()} DA de dettes` : 'Paiement à la vente'
                                                            ) : (
                                                                formatDateReadable(restaurant.currentPeriodExpiry || restaurant.subscriptionExpiry)
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}

                                        {/* ACTIONS ABONNEMENT */}
                                        {hasPendingPayment ? (
                                            <motion.button 
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setView({ screen: 'adminPayments' })}
                                                className="w-full py-4 px-4 bg-yellow-50 border-2 border-yellow-400 rounded-3xl flex items-center gap-3 shadow-sm hover:bg-yellow-100 transition-all text-left"
                                            >
                                                <div className="bg-yellow-200/50 p-3 rounded-2xl">
                                                    <Clock size={28} className="text-yellow-600" />
                                                </div>
                                                <div className="flex-1 flex flex-col">
                                                    <span className="text-sm font-black text-yellow-700 uppercase tracking-widest italic leading-tight mb-1">
                                                        Demande en cours d'examen
                                                    </span>
                                                    <span className="text-[9px] font-bold text-yellow-600 uppercase tracking-wider">
                                                        Appuyez pour voir vos règlements
                                                    </span>
                                                </div>
                                            </motion.button>
                                        ) : (
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <motion.button 
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => setView({ 
                                                        screen: 'adminSubscription', 
                                                        params: { 
                                                            mode: restaurant.subscriptionType !== 'commission' ? 'renew_fixed' : 'renew',
                                                            isCumulative: true,
                                                            activeTab: 'subscription',
                                                            planType: restaurant.subscriptionType
                                                        } 
                                                    })}
                                                    className="flex-1 py-5 bg-red-600 text-white rounded-3xl font-black text-xs uppercase tracking-[0.2em] italic shadow-lg hover:bg-red-700 transition-all"
                                                >
                                                    Renouveler
                                                </motion.button>
                                                <motion.button 
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => {
                                                        if (restaurant.subscriptionType === 'commission') {
                                                            setView({ screen: 'adminSubscription', params: { mode: 'switch_to_fixed', activeTab: 'subscription', debt: currentFees + Number(restaurant.commissionDebt || 0), isCumulative: true } });
                                                        } else {
                                                            setView({ screen: 'adminSubscription', params: { mode: 'switch_to_commission', activeTab: 'subscription' } });
                                                        }
                                                    }}
                                                    className="flex-1 py-5 bg-red-50 text-red-600 rounded-3xl font-black text-xs uppercase tracking-[0.2em] italic shadow-sm border border-red-100 hover:bg-red-100 transition-all"
                                                >
                                                    Changer plan
                                                </motion.button>
                                            </div>
                                        )}
                                    </div>

                                    {/* 2. OPTION EN VEDETTE */}
                                    <div className="space-y-4 pt-8 border-t border-gray-100">
                                        <div className="flex items-center justify-between px-2">
                                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Visibilité Boostée</h3>
                                            {isFeaturedActive && (
                                                <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase italic border border-blue-100">
                                                    <Rocket size={12} /> En Vedette
                                                </span>
                                            )}
                                        </div>

                                        <motion.div 
                                            initial={{ x: 20, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            className="bg-[#0052CC] rounded-3xl p-6 pt-10 shadow-lg relative z-10 border-b-4 border-black/10 overflow-hidden"
                                        >
                                            <div className="flex justify-between items-center">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-white/70 uppercase tracking-widest mb-1">EN VEDETTE</span>
                                                    <h3 className="text-xl font-black text-white uppercase italic tracking-tighter">
                                                        {isFeaturedActive ? "Visibilité En Vedette" : "Visibilité Standard"}
                                                    </h3>
                                                </div>
                                                <div className="text-right">
                                                    {isFeaturedActive ? (
                                                        <span className="text-xl font-black text-white italic tracking-tighter">
                                                            {getTimeRemaining(restaurant.featuredExpiry) || 'Expiré'}
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-black text-white/50 uppercase tracking-widest">Inactif</span>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>

                                        <div className="space-y-3">
                                            <motion.button 
                                                whileTap={{ scale: 0.98 }}
                                                disabled={restaurant.status === 'suspended' || restaurant.subscriptionStatus === 'expired'}
                                                onClick={() => setView({ screen: 'adminSubscription', params: { activeTab: 'featured', mode: restaurant.subscriptionType !== 'commission' ? 'renew_fixed' : 'renew' } })}
                                                className={`w-full py-5 rounded-3xl flex items-center justify-center shadow-sm transition-all font-black uppercase tracking-widest italic text-xs ${(restaurant.status === 'suspended' || restaurant.subscriptionStatus === 'expired') ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border-2 border-[#0052CC] text-[#0052CC] hover:bg-blue-50'}`}
                                            >
                                                {isFeaturedActive ? "Prolonger En Vedette" : "S'abonner En Vedette"}
                                            </motion.button>
                                            
                                            <motion.button 
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setView({ screen: 'adminPayments' })}
                                                className="w-full py-5 bg-green-600 text-white border-2 border-green-600 rounded-3xl flex items-center justify-center shadow-lg shadow-green-100 hover:bg-green-700 transition-all font-black uppercase tracking-widest italic text-xs"
                                            >
                                                <HistoryIcon size={16} className="mr-2" /> Historique des Paiements
                                            </motion.button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>


            <form onSubmit={handleSaveChanges} className="space-y-4">

                {/* --- CARD: STATUT EN LIGNE (STYLE PREMIUM) --- */}
                <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className={`rounded-[2.5rem] shadow-xl overflow-hidden border p-8 flex items-center justify-between transition-all ${formData.isOnline ? 'bg-white border-green-100 shadow-green-100/20' : 'bg-gray-50 border-gray-200 shadow-gray-200/20'}`}
                >
                    <div className="flex items-center gap-5">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all ${formData.isOnline ? 'bg-green-500 text-white shadow-green-200' : 'bg-gray-200 text-gray-500 shadow-gray-100'}`}>
                            <Store className="w-7 h-7" />
                        </div>
                        <div className="text-left">
                            <h2 className="text-lg font-black text-gray-900 uppercase italic tracking-tighter">
                                {formData.isOnline ? 'Restaurant En Ligne' : 'Restaurant Hors Ligne'}
                            </h2>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-1">
                                {formData.isOnline ? 'Menu visible & commandes actives' : 'Caché de l\'application'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {isUpdatingOnline && <Loader2 className="w-5 h-5 text-green-500 animate-spin" />}
                        <label className={`relative inline-flex items-center ${restaurant.status === 'suspended' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                            <input 
                                type="checkbox" 
                                name="isOnline" 
                                checked={restaurant.status === 'suspended' ? false : formData.isOnline} 
                                onChange={restaurant.status === 'suspended' ? undefined : handleToggleOnline} 
                                className="sr-only peer" 
                                disabled={restaurant.status === 'suspended' || isUpdatingOnline}
                            />
                            <div className="w-16 h-8 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500 shadow-inner"></div>
                        </label>
                    </div>
                </motion.div>

                {/* --- CARD: IDENTITÉ (STYLE PREMIUM) --- */}
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100 mb-6">
                    <button type="button" onClick={() => toggleSection('identity')} className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-200">
                                <Store className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Identité Visuelle</span>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-0.5">Nom, logo & slogan</p>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'identity' ? 'bg-red-600 text-white rotate-180 shadow-md shadow-red-100' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {expandedSection === 'identity' && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-8 border-t border-gray-50 space-y-6">
                                    {/* Inputs Layout */}
                                    <div className="grid grid-cols-1 gap-5">
                                        <div>
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom du propriétaire</label>
                                            <input 
                                                type="text" 
                                                name="ownerName" 
                                                value={formData.ownerName} 
                                                onChange={handleChange} 
                                                placeholder="Prénom et Nom"
                                                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Nom de l'établissement</label>
                                            <input 
                                                type="text" 
                                                name="name" 
                                                value={formData.name} 
                                                onChange={handleChange} 
                                                placeholder="Ex: Le Gourmet d'Alger"
                                                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Slogan accrocheur</label>
                                            <input 
                                                type="text" 
                                                name="slogan" 
                                                value={formData.slogan} 
                                                onChange={handleChange} 
                                                placeholder="Ex: Le goût authentique"
                                                className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium" 
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Wilaya</label>
                                                <div className="relative">
                                                    <select 
                                                        name="wilaya" 
                                                        value={formData.wilaya} 
                                                        onChange={handleChange} 
                                                        className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium appearance-none cursor-pointer"
                                                    >
                                                        {wilayas.map(w => <option key={w} value={w}>{w}</option>)}
                                                    </select>
                                                    <ChevronRight className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 rotate-90 pointer-events-none" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Téléphone public</label>
                                                <input 
                                                    type="tel" 
                                                    name="phone" 
                                                    value={formData.phone} 
                                                    onChange={handleChange} 
                                                    placeholder="05 / 06 / 07 ..."
                                                    className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                                />
                                            </div>
                                        </div>

                                    </div>

                                    {/* Cover Photo Area with Premium Button */}
                                    <div className="pt-4">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-3 block">Photo de Couverture</label>
                                        <div className="flex flex-col gap-4">
                                            <div className="relative group overflow-hidden rounded-[2rem] border-2 border-dashed border-gray-100 aspect-video bg-gray-50">
                                                {formData.image ? (
                                                    <img src={formData.image} alt="Cover" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center h-full">
                                                        <Store size={40} className="text-gray-200 mb-2" />
                                                        <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">Aucun visuel</span>
                                                    </div>
                                                )}
                                                <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center cursor-pointer backdrop-blur-[2px]">
                                                    <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                                                    <div className="bg-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest text-gray-900 flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all">
                                                        {isUploadingCover ? 'Téléchargement...' : 'Modifier la photo'}
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100 mb-6">
                    <button type="button" onClick={() => toggleSection('delivery')} className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-200">
                                <MapPin className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Livraison & Secteurs</span>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-0.5">Géolocalisation & livreurs</p>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'delivery' ? 'bg-red-600 text-white rotate-180 shadow-md shadow-red-100' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {expandedSection === 'delivery' && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-8 border-t border-gray-50 space-y-8">
                                    {/* Maps Section */}
                                    <div className="space-y-4">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 mb-1 block">Emplacement GPS</label>
                                        <div className="grid grid-cols-1 gap-4">
                                            <motion.button
                                                type="button"
                                                whileTap={{ scale: 0.98 }}
                                                disabled={isLocating}
                                                onClick={async () => {
                                                    setIsLocating(true);
                                                    try {
                                                        const pos = await getCurrentPosition();
                                                        if (pos) {
                                                            setFormData(prev => ({ ...prev, location: { lat: pos.lat, lng: pos.lng } }));
                                                        }
                                                    } finally {
                                                        setIsLocating(false);
                                                    }
                                                }}
                                                className={`w-full flex items-center justify-center gap-3 p-5 rounded-2xl font-black text-xs uppercase tracking-widest italic transition-all shadow-xl ${formData.location ? 'bg-green-600 text-white shadow-green-100' : 'bg-red-600 text-white hover:bg-red-700 shadow-red-100'}`}
                                            >
                                                <MapPin className={`w-5 h-5 ${isLocating ? 'animate-bounce' : ''}`} />
                                                {isLocating ? 'Géolocalisation...' : formData.location ? 'Position Enregistrée ✓' : 'Ma Position Actuelle'}
                                            </motion.button>
                                            
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-1 block">Latitude</label>
                                                    <input 
                                                        type="number" 
                                                        step="any" 
                                                        value={formData.location?.lat || ''} 
                                                        onChange={(e) => setFormData(prev => ({ ...prev, location: { lat: parseFloat(e.target.value) || 0, lng: prev.location?.lng || 0 } }))} 
                                                        placeholder="0.00000" 
                                                        className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium text-sm" 
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-bold text-gray-400 uppercase ml-1 mb-1 block">Longitude</label>
                                                    <input 
                                                        type="number" 
                                                        step="any" 
                                                        value={formData.location?.lng || ''} 
                                                        onChange={(e) => setFormData(prev => ({ ...prev, location: { lat: prev.location?.lat || 0, lng: parseFloat(e.target.value) || 0 } }))} 
                                                        placeholder="0.00000" 
                                                        className="w-full p-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium text-sm" 
                                                    />
                                                </div>
                                            </div>

                                            {formData.location && (
                                                <a
                                                    href={`https://www.google.com/maps/search/?api=1&query=${formData.location.lat},${formData.location.lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center gap-2 py-3 px-4 bg-gray-50 text-red-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-50 transition-colors border border-red-50"
                                                >
                                                    Ouvrir dans Google Maps <ExternalLink size={12} />
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    <hr className="border-gray-50" />

                                    {/* Delivery Toggle Section */}
                                    <div className="flex items-center justify-between p-2">
                                        <div>
                                            <h4 className="text-sm font-black text-gray-900 uppercase italic tracking-tight">Activer la livraison</h4>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Rendre l'établissement livrable</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" name="deliveryEnabled" checked={formData.deliveryEnabled} onChange={handleChange} className="sr-only peer" />
                                            <div className="w-14 h-7 bg-gray-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600 shadow-inner"></div>
                                        </label>
                                    </div>

                                    {/* Drivers Section */}
                                    {formData.deliveryEnabled && (
                                        <div className="space-y-6 pt-4">
                                            <div className="flex items-center justify-between">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 block">Mes Livreurs Habituels</label>
                                                <span className="bg-red-50 text-red-600 text-[10px] font-black px-2 py-0.5 rounded-full">{formData.deliveryDrivers?.length || 0}</span>
                                            </div>
                                            
                                            <div className="grid grid-cols-1 gap-3">
                                                {formData.deliveryDrivers && formData.deliveryDrivers.length > 0 ? (
                                                    formData.deliveryDrivers.map((driver, index) => (
                                                        <motion.div 
                                                            key={index} 
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            className="flex justify-between items-center p-5 bg-gray-50/50 rounded-2xl border border-gray-100"
                                                        >
                                                            <div className="flex items-center gap-4">
                                                                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center text-red-600 font-black shadow-sm border border-gray-100">
                                                                    {driver.name.charAt(0)}
                                                                </div>
                                                                <div>
                                                                    <p className="text-sm font-black text-gray-900 uppercase italic">{driver.name}</p>
                                                                    <div className="flex items-center gap-3 mt-0.5">
                                                                        <p className="text-[10px] font-bold text-gray-400 tabular-nums">{driver.phone}</p>
                                                                        {(freshDriverRatings[driver.id]?.rating !== undefined) && (
                                                                            <div className="flex items-center gap-1 text-red-600 font-bold text-[10px]">
                                                                                <span>⭐</span>
                                                                                <span>{freshDriverRatings[driver.id].rating}</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button type="button" onClick={() => handleDeleteDriver(index)} className="text-red-400 hover:text-red-600 p-2.5 bg-white rounded-xl transition-colors border border-gray-100 shadow-sm">
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </motion.div>
                                                    ))
                                                ) : (
                                                    <div className="p-10 bg-gray-50/50 rounded-[2rem] border-2 border-dashed border-gray-100 text-center">
                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic leading-relaxed">Aucun livreur habituel<br/>enregistré pour le moment.</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Search Driver */}
                                            <div className="pt-4 space-y-4">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider ml-1 block">Ajouter un livreur (Recherche)</label>
                                                <div className="flex gap-3">
                                                    <div className="relative flex-1">
                                                        <Phone className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                                                        <input 
                                                            type="text" 
                                                            value={searchTerm} 
                                                            onChange={(e) => setSearchTerm(e.target.value)} 
                                                            placeholder="N° de téléphone..." 
                                                            className="w-full pl-12 pr-6 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 font-medium"
                                                        />
                                                    </div>
                                                    <motion.button 
                                                        whileTap={{ scale: 0.95 }}
                                                        type="button" 
                                                        onClick={handleSearchDrivers} 
                                                        disabled={isSearching} 
                                                        className="px-8 bg-gray-900 text-white font-black text-[10px] uppercase italic tracking-widest rounded-2xl disabled:opacity-50 shadow-lg shadow-gray-200"
                                                    >
                                                        {isSearching ? '...' : 'Chercher'}
                                                    </motion.button>
                                                </div>
                                                
                                                <AnimatePresence>
                                                    {searchResults.length > 0 && (
                                                        <motion.div 
                                                            initial={{ opacity: 0, y: 10 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            className="space-y-2 pt-2"
                                                        >
                                                            {searchResults.map(user => (
                                                                <div key={user.uid} className="flex justify-between items-center p-5 bg-red-50/30 rounded-[2rem] border border-red-100">
                                                                    <div>
                                                                        <p className="text-sm font-black text-gray-900 uppercase italic">{user.name}</p>
                                                                        <div className="flex items-center gap-3 mt-1">
                                                                            <p className="text-xs font-bold text-gray-400 tabular-nums">{user.phone}</p>
                                                                            <div className="flex items-center gap-1 text-red-600 font-black text-xs">
                                                                                <span>⭐</span>
                                                                                <span>{user.rating || 0}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <motion.button 
                                                                        whileTap={{ scale: 0.95 }}
                                                                        type="button" 
                                                                        onClick={() => handleAddDriver(user)} 
                                                                        className="px-6 py-3 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest italic shadow-lg shadow-red-100"
                                                                    >
                                                                        Ajouter
                                                                    </motion.button>
                                                                </div>
                                                            ))}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* --- CARD: PROMOTIONS (STYLE PREMIUM) --- */}
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100 mb-6">
                    <button type="button" onClick={() => toggleSection('promotions')} className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-200">
                                <Zap className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Promotions & Remises</span>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-0.5">Réductions globales</p>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'promotions' ? 'bg-red-600 text-white rotate-180 shadow-md shadow-red-100' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {expandedSection === 'promotions' && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-8 border-t border-gray-50">
                                    <div className="p-8 bg-gray-50/50 rounded-[2.5rem] border border-gray-100 space-y-8">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Remise Globale</span>
                                            <div className="bg-red-600 px-4 py-1.5 rounded-full shadow-lg shadow-red-100">
                                                <span className="text-white font-black text-sm italic">-{formData.globalDiscount}%</span>
                                            </div>
                                        </div>
                                        
                                        <div className="space-y-4">
                                            <input 
                                                type="range" 
                                                name="globalDiscount" 
                                                min="0" 
                                                max="70" 
                                                step="5" 
                                                value={formData.globalDiscount} 
                                                onChange={handleChange} 
                                                className="w-full h-3 bg-white border border-gray-100 rounded-full appearance-none cursor-pointer accent-red-600" 
                                            />
                                            <div className="flex justify-between px-1">
                                                <span className="text-[10px] font-black text-gray-300">0%</span>
                                                <span className="text-[10px] font-black text-gray-300">70% (MAX)</span>
                                            </div>
                                        </div>

                                        <p className="text-[10px] text-gray-400 italic font-bold leading-relaxed bg-white/50 p-4 rounded-2xl border border-gray-50">
                                            ⚠️ Cette remise s'appliquera automatiquement sur TOUS les articles de votre menu visibles par les clients.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* --- CARD: HORAIRES (STYLE PREMIUM) --- */}
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100 mb-6">
                    <button type="button" onClick={() => toggleSection('hours')} className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-200">
                                <Clock className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Horaires d'ouverture</span>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-0.5">Mise en ligne automatique</p>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'hours' ? 'bg-red-600 text-white rotate-180 shadow-md shadow-red-100' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>

                    <AnimatePresence>
                        {expandedSection === 'hours' && (
                            <motion.div 
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                            >
                                <div className="p-4 sm:p-8 border-t border-gray-50 space-y-6">
                                    <div className="p-4 sm:p-6 bg-gray-50/50 rounded-[1.5rem] sm:rounded-[2rem] border border-gray-100 flex items-center justify-between">
                                        <div className="text-left">
                                            <h4 className="text-sm font-black text-gray-900 uppercase italic tracking-tight">Planning Automatique</h4>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Activer l'ouverture programmée</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={formData.useSchedule} 
                                                onChange={(e) => setFormData(prev => ({ ...prev, useSchedule: e.target.checked }))} 
                                                className="sr-only peer" 
                                            />
                                            <div className="w-14 h-7 bg-gray-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-600 shadow-inner"></div>
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-1 gap-3">
                                        {Object.entries(formData.openingHours).map(([day, hours]: [string, any]) => {
                                            const dayNames: Record<string, string> = {
                                                monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
                                                thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche'
                                            };
                                            const isClosed = hours.isClosed;
                                            return (
                                                <div key={day} className={`p-4 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] border transition-all ${isClosed ? 'bg-gray-50/30 border-gray-100' : 'bg-white border-red-50 shadow-sm'}`}>
                                                    <div className="flex items-center justify-between">
                                                        <span className={`text-sm font-black uppercase italic tracking-tight ${isClosed ? 'text-gray-300' : 'text-gray-900'}`}>{dayNames[day]}</span>
                                                        <label className="flex items-center gap-2 cursor-pointer group">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={isClosed}
                                                                    onChange={(e) => {
                                                                        const newHours = { ...formData.openingHours };
                                                                        newHours[day as keyof OpeningHours] = { ...hours, isClosed: e.target.checked };
                                                                        setFormData(prev => ({ ...prev, openingHours: newHours }));
                                                                    }}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="w-10 h-5 bg-gray-100 rounded-full peer-checked:bg-gray-400 transition-all"></div>
                                                                <div className="absolute top-0.5 left-[2px] w-4 h-4 bg-white rounded-full peer-checked:translate-x-5 transition-all shadow-sm"></div>
                                                            </div>
                                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic group-hover:text-gray-600 transition-colors">Fermé</span>
                                                        </label>
                                                    </div>
                                                    {!isClosed && (
                                                        <div className="flex items-center justify-center gap-4 mt-4 sm:mt-6">
                                                            <div className="w-24 sm:w-32">
                                                                <label className="text-[7px] sm:text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block text-center">Ouverture</label>
                                                                <input
                                                                    type="time"
                                                                    value={hours.open}
                                                                    onChange={(e) => {
                                                                        const newHours = { ...formData.openingHours };
                                                                        newHours[day as keyof OpeningHours] = { ...hours, open: e.target.value };
                                                                        setFormData(prev => ({ ...prev, openingHours: newHours }));
                                                                    }}
                                                                    className="w-full p-2 sm:p-4 bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 text-center"
                                                                />
                                                            </div>
                                                            <div className="text-gray-200 mt-6 font-light">|</div>
                                                            <div className="w-24 sm:w-32">
                                                                <label className="text-[7px] sm:text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5 block text-center">Fermeture</label>
                                                                <input
                                                                    type="time"
                                                                    value={hours.close}
                                                                    onChange={(e) => {
                                                                        const newHours = { ...formData.openingHours };
                                                                        newHours[day as keyof OpeningHours] = { ...hours, close: e.target.value };
                                                                        setFormData(prev => ({ ...prev, openingHours: newHours }));
                                                                    }}
                                                                    className="w-full p-2 sm:p-4 bg-gray-50 border border-gray-100 rounded-xl sm:rounded-2xl font-black text-[10px] sm:text-xs uppercase focus:outline-none focus:ring-2 focus:ring-red-600 focus:bg-white transition-all text-gray-900 text-center"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* --- CARD: RESERVATIONS (STYLE PREMIUM) --- */}
                <div className="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/40 overflow-hidden border border-gray-100 mb-6">
                    <button type="button" onClick={() => toggleSection('reservations')} className="w-full p-8 flex items-center justify-between bg-white hover:bg-gray-50 transition-all">
                        <div className="flex items-center gap-5">
                            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-lg shadow-gray-200">
                                <Calendar className="w-7 h-7" />
                            </div>
                            <div className="text-left">
                                <span className="text-[11px] font-black text-gray-900 uppercase italic tracking-tighter">Réservation de Tables</span>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-tight mt-0.5">Capacité & Algorithme</p>
                            </div>
                        </div>
                        <div className={`p-2.5 rounded-xl transition-all ${expandedSection === 'reservations' ? 'bg-gray-900 text-white rotate-180 shadow-md shadow-gray-200' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRight size={20} />
                        </div>
                    </button>
                    <AnimatePresence>
                        {expandedSection === 'reservations' && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="overflow-hidden"
                            >
                                <div className="p-8 pt-0 border-t border-gray-50 space-y-8 bg-gray-50/30">
                                    <div className="p-6 bg-white rounded-[2rem] border border-gray-100 shadow-sm flex items-center justify-between group hover:border-gray-200 hover:shadow-md transition-all">
                                        <div>
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-1">Système Capacitaire</label>
                                            <p className="text-xs font-bold text-gray-900 leading-tight">Activer les réservations</p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                name="tableReservationEnabled"
                                                checked={formData.tableReservationEnabled} 
                                                onChange={handleChange} 
                                                className="sr-only peer" 
                                            />
                                            <div className="w-14 h-8 bg-gray-100 rounded-full peer-checked:bg-gray-900 transition-all after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:after:translate-x-6 shadow-inner"></div>
                                        </label>
                                    </div>
                                    
                                    {formData.tableReservationEnabled && formData.reservationSettings && (
                                        <div className="space-y-6">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-2">Total Tables</label>
                                                    <input
                                                        type="number"
                                                        value={formData.reservationSettings.totalTables}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                reservationSettings: {
                                                                    ...prev.reservationSettings!,
                                                                    totalTables: parseInt(e.target.value) || 0
                                                                }
                                                            }));
                                                        }}
                                                        className="w-full p-5 bg-white border border-gray-100 rounded-2xl font-black text-sm uppercase focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all placeholder:text-gray-300 text-gray-900 shadow-sm"
                                                        placeholder="Ex: 10"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-2">Capacité Max (Personnes)</label>
                                                    <input
                                                        type="number"
                                                        value={formData.reservationSettings.maxCapacityPerSlot}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                reservationSettings: {
                                                                    ...prev.reservationSettings!,
                                                                    maxCapacityPerSlot: parseInt(e.target.value) || 0
                                                                }
                                                            }));
                                                        }}
                                                        className="w-full p-5 bg-white border border-gray-100 rounded-2xl font-black text-sm uppercase focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all placeholder:text-gray-300 text-gray-900 shadow-sm"
                                                        placeholder="Ex: 40"
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-2">Durée Moy. (Min)</label>
                                                    <input
                                                        type="number"
                                                        value={formData.reservationSettings.slotDurationMinutes}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                reservationSettings: {
                                                                    ...prev.reservationSettings!,
                                                                    slotDurationMinutes: parseInt(e.target.value) || 0
                                                                }
                                                            }));
                                                        }}
                                                        className="w-full p-5 bg-white border border-gray-100 rounded-2xl font-black text-sm uppercase focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all placeholder:text-gray-300 text-gray-900 shadow-sm"
                                                        placeholder="Ex: 90"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3 ml-2">Délai Anti. (Min)</label>
                                                    <input
                                                        type="number"
                                                        value={formData.reservationSettings.advanceNoticeMin}
                                                        onChange={(e) => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                reservationSettings: {
                                                                    ...prev.reservationSettings!,
                                                                    advanceNoticeMin: parseInt(e.target.value) || 0
                                                                }
                                                            }));
                                                        }}
                                                        className="w-full p-5 bg-white border border-gray-100 rounded-2xl font-black text-sm uppercase focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all placeholder:text-gray-300 text-gray-900 shadow-sm"
                                                        placeholder="Ex: 60"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

            </form>
            <div className="px-2 mt-10 pb-8 text-center">
                <button
                    onClick={logout}
                    className="w-full py-4 bg-red-50 text-red-600 font-black rounded-2xl shadow-sm border border-red-100 hover:bg-red-100 transition-all active:scale-95"
                >
                    SE DÉCONNECTER
                </button>
            </div>

            {/* --- BOUTON FLOTTANT D'ENREGISTREMENT --- */}
            <AnimatePresence>
                {isDirty && (
                    <motion.div
                        initial={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 100, opacity: 0 }}
                        className="fixed bottom-8 left-0 right-0 z-[100] px-4 flex justify-center pointer-events-none"
                    >
                        <div className="w-full max-w-[280px] pointer-events-auto">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleSaveChanges}
                                disabled={isSaving}
                                className="w-full py-3 bg-green-600 text-white rounded-full font-black uppercase tracking-widest italic shadow-2xl shadow-green-200/60 border-2 border-white/20 flex items-center justify-center gap-2 backdrop-blur-sm"
                            >
                                {isSaving ? (
                                    <Loader2 className="animate-spin" size={16} />
                                ) : (
                                    <Save size={16} />
                                )}
                                <span className="text-[10px]">{isSaving ? "Chargement..." : "Enregistrer"}</span>
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminAccountScreen;